// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
//
// ─────────────────────────────────────────────────────────────────────────────
// LIRON BOARD — Piper's review surface for Tailormind Liron.
//
// Erez, 30 Aug 2026: "Piper needs a board. Create one in the actual dashboard
// and then put it in the agent board so she actually has a board."
//
// ⛔ ONE SECTION TODAY, AND THAT IS HONEST RATHER THAN UNFINISHED. The board law
// allows three kinds of section — a KB page PRESENTED, a presentation, or a
// diagram — and nothing else. Nothing of this engagement is built yet, so there
// is exactly one thing true enough to show: the founding document, which states
// the mission, the two questions Erez deliberately left open, and what Piper's
// first run is.
//
// ⛔ AND BOARDS ARE CUMULATIVE. Piper ADDS sections as she produces work. This
// file is the shape she extends; it is not a placeholder to be swept away, and
// nothing comes off it without Erez's word.
//
// If you want to change what this board SAYS, edit the KB page — not this file.
// The page is the content and the board is the frame; that separation is the
// whole point of DocumentFrame.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { DocumentFrame } from "./DocumentFrame";

/** Matches the document height the other project boards use, so a reader moving
 *  between boards is not re-learning the furniture on each one. */
const DOC_H = 1240;

/** 4400, not 3400. A SectionBox hugs its content plus ~200px of padding each
 *  side, so a 900-wide DocumentFrame makes a box ~1,985px. Two need ~4,160 to
 *  sit side by side, and at 3400 the second wrapped underneath — turning the
 *  board into a tall column, which the board law explicitly forbids ("no tall
 *  sections… wider than tall"). Widened to 6400 on 30 Aug when the third
 *  section landed. Widen again for a fourth rather than letting it stack. Measured in the browser, not guessed. */
const BOARD_W = 6400;

function RecordNode() {
  return (
    <SectionGrid width={BOARD_W} gap={190}>
      <SectionBox title="The engagement" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/tailormind-liron/overview"
            label="THE ENGAGEMENT — mission, the open questions, the first run"
            width={900}
            height={DOC_H}
          />
        </div>
      </SectionBox>

      {/* ADDED 30 Aug 2026 — Erez asked for the CEO shape to be productised into a
          golden image, with Liron as the first customer, and said "analyse
          everything and tell me what you think." This is the answer. It is a KB
          page PRESENTED, exactly like the section above: the page is the content,
          this file is only the frame. */}
      <SectionBox title="Productising the CEO shape" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/tailormind-liron/productising-the-ceo-shape"
            label="THE ANALYSIS — why Erez's box is the wrong parent, and the three phases"
            width={900}
            height={DOC_H}
          />
        </div>
      </SectionBox>

      {/* ADDED 30 Aug 2026 — Erez: "can you just write the plan here?" Five phases,
          a test on every step. Third section, so the grid widens again. */}
      <SectionBox title="The plan" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/tailormind-liron/the-plan"
            label="THE PLAN — cut the template, stand up her box, four agents, then write the skill"
            width={900}
            height={DOC_H}
          />
        </div>
      </SectionBox>
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = {
  record: RecordNode as unknown as NodeTypes[string],
};

export function LironBoardPage() {
  // useMemo([]) is the board pattern: build the node list ONCE, so an unrelated
  // re-render never remounts a live frame inside it.
  const nodes = useMemo<Node[]>(
    () => [{ id: "record", type: "record", position: { x: 0, y: 0 }, draggable: false, data: {} }],
    [],
  );

  return <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.06} />;
}

export default LironBoardPage;
